import React from 'react';
import { Alert } from 'antd';

import LeftBehindPanel from './LeftBehindPanel';
import {
	BlockedPanel,
	DependentsPanel,
	GiveValuePanel,
	NumbersPanel,
	PreviewPanel,
	RulesPanel,
	SourcesPanel,
	WarningsPanel,
} from './ReviewPanels';
import { BLOCKED_FIRST, REVIEW_HEADING, confirmSentence, giveValueFirst } from './reviewCopy';

export const BLOCKED_CODE = 'change_card_blocked_by_lookups';

/**
 * Whether the change may be confirmed now, and if not the one sentence that
 * says why (the Change card button's tooltip): `{ ok, reason }`. Only a dry
 * run answer for this very body, not blocked, with a token, every required
 * value given, and no move on its way.
 */
export function confirmState({ dry, missing = [], applying = false }) {
	if (dry?.error?.code === BLOCKED_CODE) return { ok: false, reason: BLOCKED_FIRST };
	if (missing.length) return { ok: false, reason: giveValueFirst(missing) };
	if (applying || !dry?.fresh || !dry?.data?.confirm?.token) return { ok: false, reason: null };
	return { ok: true, reason: null };
}

/**
 * CHANGE CARD — the review step (KPE-028, KPR-709). The dry run's answer in
 * words a person understands, before anything changes: the one sentence the
 * Change card button confirms, what will move and what will not, where each
 * element goes, what is left behind and what is lost, the values still to
 * give, the lookups that block it, the rules on the new card, what stops
 * applying, and a few items before and after. The page owns the state and
 * the button; this draws it.
 */
export default function ReviewStep({
	dry,
	ctx,
	cardsById,
	sourcesByName,
	targetsByName,
	giveElements,
	asks,
	askErrors,
	onAsk,
	onTick,
	onLeftBehind,
	refusal,
	missing,
}) {
	const data = dry?.data || null;
	const blocked = dry?.error?.code === BLOCKED_CODE ? dry.error.data : null;
	const otherError = dry?.error && !blocked ? dry.error : null;
	const showCounts = !!data && !blocked;

	return (
		<div className={`change-card__review${dry?.loading ? ' is-refreshing' : ''}`} aria-busy={!!dry?.loading}>
			<section className="change-card__panel change-card__confirm" aria-label="The change">
				<h2 className="change-card__review-heading">{REVIEW_HEADING}</h2>
				<p className="change-card__lead" aria-live="polite">
					{showCounts && !missing.length ? confirmSentence(data.items, data.warnings, ctx) : (missing.length ? giveValueFirst(missing) : null)}
				</p>
			</section>

			{refusal ? <Alert type="error" showIcon message={refusal.text} className="change-card__refusal" /> : null}
			{otherError ? <Alert type="error" showIcon message={otherError.message} /> : null}
			{blocked ? (
				<BlockedPanel refusal={blocked} ctx={ctx} cardsById={cardsById} onRetry={dry.retry} retrying={dry.loading} />
			) : null}

			<GiveValuePanel elements={giveElements} values={asks} errors={askErrors} onChange={onAsk} ctx={ctx} />

			{showCounts ? (
				<>
					<NumbersPanel data={data} ctx={ctx} />
					<SourcesPanel data={data} ctx={ctx} />
					<LeftBehindPanel unmapped={data.unmapped || []} onChange={onLeftBehind} ctx={ctx} />
					<WarningsPanel warnings={data.warnings} ctx={ctx} />
					<RulesPanel rules={data.rules || []} keeprWrites={data.keeprWrites || []} onTick={onTick} ctx={ctx} />
					<DependentsPanel dependents={data.dependents} ctx={ctx} />
					<PreviewPanel preview={data.preview || []} ctx={ctx} sourcesByName={sourcesByName} targetsByName={targetsByName} />
				</>
			) : null}
		</div>
	);
}
